/**
 * ChatMarkdown — lightweight, chat-bubble-sized markdown renderer shared by
 * the AI assistant bubble and the full /ai-assistant page, so replies with
 * headings, bold text, lists and tables render properly instead of as raw
 * markdown syntax.
 */
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChatLink, ChatImage, chatUrlTransform } from './duti/ChatMedia';

const components = {
  p: ({ children }) => <div className="mb-2 last:mb-0">{children}</div>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  ul: ({ children }) => <ul className="list-disc pl-4 mb-2 space-y-0.5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-4 mb-2 space-y-0.5">{children}</ol>,
  li: ({ children }) => <li>{children}</li>,
  h1: ({ children }) => <h3 className="font-semibold text-sm mt-1 mb-1">{children}</h3>,
  h2: ({ children }) => <h3 className="font-semibold text-sm mt-1 mb-1">{children}</h3>,
  h3: ({ children }) => <h4 className="font-semibold text-sm mt-1 mb-1">{children}</h4>,
  code: ({ children }) => <code className="bg-black/10 dark:bg-white/10 rounded px-1 py-0.5 text-xs">{children}</code>,
  table: ({ children }) => (
    <div className="overflow-x-auto mb-2">
      <table className="text-xs border-collapse w-full">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-black/5 dark:bg-white/5">{children}</thead>,
  th: ({ children }) => <th className="border px-2 py-1 text-left font-medium">{children}</th>,
  td: ({ children }) => <td className="border px-2 py-1 align-top">{children}</td>,
  img: ({ src, alt }) => <ChatImage src={src} alt={alt} />,
};

const withLinks = { ...components, a: ({ children, href }) => <ChatLink href={href} internalLinks>{children}</ChatLink> };
const withoutLinks = { ...components, a: ({ children, href }) => <ChatLink href={href}>{children}</ChatLink> };

export const ChatMarkdown = ({ content, internalLinks = true }) => (
  <div className="text-sm leading-relaxed [&_table]:my-1">
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={internalLinks ? withLinks : withoutLinks} urlTransform={chatUrlTransform}>
      {content}
    </ReactMarkdown>
  </div>
);
